import { useState, type ChangeEvent, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../lib/cn";
import { useFieldControl } from "./field";

export interface SliderProps
  extends Omit<ComponentPropsWithRef<"input">, "type" | "value" | "defaultValue" | "onChange"> {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  onValueChange?: (value: number) => void;
  /** Affiche la valeur courante au-dessus de la piste. */
  showValue?: boolean;
  /** Mise en forme de la valeur affichée (unité, devise…). */
  formatValue?: (value: number) => ReactNode;
  /** Repères affichés sous la piste. */
  marks?: { value: number; label: ReactNode }[];
}

/**
 * Curseur de valeur, bâti sur `<input type="range">`.
 *
 * Le natif apporte gratuitement les flèches du clavier, Page↑/Page↓, le
 * glissement tactile et la sémantique ARIA complète — tout ce qu'une
 * réimplémentation en `div` doit refaire, et fait généralement à moitié.
 *
 * Seule l'apparence est reprise : piste, remplissage et pouce sont dessinés
 * avec les pseudo-éléments, propres à chaque moteur, d'où la duplication
 * `-webkit-` / `-moz-`.
 */
export function Slider({
  className,
  value,
  defaultValue = 50,
  min = 0,
  max = 100,
  step = 1,
  onValueChange,
  showValue,
  formatValue,
  marks,
  disabled,
  ...props
}: SliderProps) {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;

  const fieldProps = useFieldControl(props);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value);
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
  };

  const ratio = max === min ? 0 : (current - min) / (max - min);

  return (
    <div className={cn("flex w-full flex-col gap-2", className)}>
      {showValue && (
        <div className="flex justify-end">
          <span className="rounded-sm bg-surface-sunken px-2 py-0.5 font-mono text-xs text-fg tabular-nums">
            {formatValue ? formatValue(current) : current}
          </span>
        </div>
      )}

      {/*
        La piste et son remplissage sont dessinés SOUS le champ natif, qui
        devient transparent et ne garde que son pouce.

        Le remplissage était auparavant un dégradé à arêtes franches posé sur le
        fond du champ : chaque déplacement du pouce en recalculait les butées et
        repeignait toute la piste, sur le fil principal, à la fréquence des
        événements pointeur. Ici la barre est peinte UNE fois sur toute sa
        longueur et révélée par un `scaleX` — une transformation, donc rien de
        plus qu'une matrice envoyée au compositeur pendant tout le glissement.
      */}
      <div className="relative w-full">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-border"
        >
          <span
            className={cn(
              "block h-full w-full origin-left transform-gpu rounded-full will-change-transform",
              disabled ? "bg-border-strong" : "bg-accent",
            )}
            style={{ transform: `scaleX(${ratio})` }}
          />
        </span>

        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={current}
          onChange={handleChange}
          disabled={disabled}
          className={cn(
            "relative h-1.5 w-full cursor-pointer appearance-none rounded-full bg-transparent",
            "focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",

            // Pouce — WebKit / Blink
            "[&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none",
            "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-surface-raised",
            "[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-accent",
            "[&::-webkit-slider-thumb]:shadow-sm",
            "[&::-webkit-slider-thumb]:transform-gpu",
            "[&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-fast",
            // Il grossit sous le doigt : le pouce reste visible sous le curseur.
            "active:[&::-webkit-slider-thumb]:scale-125",

            // Pouce — Gecko
            "[&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:appearance-none",
            "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-surface-raised",
            "[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-accent",
            "[&::-moz-range-thumb]:shadow-sm",
          )}
          {...fieldProps}
        />
      </div>

      {marks && (
        <div className="flex justify-between text-[11px] text-fg-muted">
          {marks.map((mark) => (
            <span key={mark.value} className="tabular-nums">
              {mark.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
